import { whenUserInteractsOrTimeout } from './utils/whenUserInteractsOrTimeout';
import { applyConsentDefaults } from './lib/adsConsent';

// This config identifies the Firebase project to Google's servers — it is
// not a secret (it's embedded in every client bundle by design, visible via
// devtools on any Firebase site) and is safe to commit. Firebase's actual
// security comes from Firestore/API key restrictions, not from hiding this.
const firebaseConfig = {
  apiKey: 'AIzaSyDUvIKP_ouau6ZpPuSjQQ7FgJBaAQqkEn4',
  authDomain: 'devstackkit.firebaseapp.com',
  projectId: 'devstackkit',
  storageBucket: 'devstackkit.firebasestorage.app',
  messagingSenderId: '596235807740',
  appId: '1:596235807740:web:a8586c6b2f24ef5b142079',
  measurementId: 'G-1NM26DD0KD',
};

export type PageViewParams = { page_path: string; page_location: string; page_title: string };
export type PageViewLogger = (params: PageViewParams) => void;

let pageViewLogger: Promise<PageViewLogger | null> | undefined;

export type ToolErrorParams = { tool: string; category: string; message_code: string };

// Only these three fields may ever be sent: never user input, never a raw error message.
export function logToolError(params: ToolErrorParams): void {
  loadPageViewLogger()
    .then(async (logPageView) => {
      if (!logPageView) return;
      const { getAnalytics, logEvent } = await import('firebase/analytics');
      const { getApp } = await import('firebase/app');
      logEvent(getAnalytics(getApp()), 'tool_error', {
        tool: params.tool,
        category: params.category,
        message_code: params.message_code,
      });
    })
    .catch(() => undefined);
}

// Firebase (and the gtag.js it injects) is loaded with a dynamic import only
// after the first user interaction or 4 seconds, whichever comes first, keeping
// it out of the main bundle and off the critical path during initial load.
// Resolves once, cached for every caller (usePageViewTracking) rather than
// re-loading per event.
//
// getAnalytics() throws in environments without IndexedDB support (e.g. some
// privacy modes) — isSupported() guards against that. A failed load (offline,
// blocked by an ad blocker) resolves to null so page views are just skipped.
// The first page view is logged as soon as Firebase initializes.
export function loadPageViewLogger(): Promise<PageViewLogger | null> {
  pageViewLogger ??= whenUserInteractsOrTimeout(4000)
    .then(() => Promise.all([import('firebase/app'), import('firebase/analytics')]))
    .then(async ([{ initializeApp }, { getAnalytics, isSupported, logEvent }]) => {
      if (!(await isSupported())) return null;
      applyConsentDefaults();
      const analytics = getAnalytics(initializeApp(firebaseConfig));
      return (params: PageViewParams) => logEvent(analytics, 'page_view', params);
    })
    .catch(() => null);
  return pageViewLogger;
}
